import { onScopeDispose, readonly, ref, shallowRef } from "vue";
import { AppError, asAppError } from "../services/errors";
import type { AppServices, Diagnostics, MailboxMessage } from "../services/types";

export interface ReportContext {
  message: MailboxMessage;
  diagnostics: Diagnostics;
}

export function useSession(services: AppServices, setLocale: (locale: string) => void) {
  const status = ref<"initializing" | "ready" | "error">("initializing");
  const error = shallowRef<AppError | null>(null);
  const context = shallowRef<ReportContext | null>(null);
  let initializing = false;
  let disposed = false;
  onScopeDispose(() => {
    disposed = true;
  });

  async function initialize() {
    if (initializing || disposed) return;
    initializing = true;
    status.value = "initializing";
    error.value = null;
    try {
      await services.mailbox.ready();
      if (disposed) return;
      setLocale(services.mailbox.getLocale());
      await services.auth.initialize();
      if (disposed) return;
      const message = await services.mailbox.getSelectedMessage();
      if (disposed) return;
      context.value = {
        message,
        diagnostics: services.mailbox.getDiagnostics(),
      };
      status.value = "ready";
    } catch (cause) {
      if (disposed) return;
      error.value = asAppError(cause, new AppError("HOST-001", "error_host"));
      services.captureError(error.value);
      status.value = "error";
    } finally {
      initializing = false;
    }
  }

  return {
    status: readonly(status),
    error: readonly(error),
    context: readonly(context),
    initialize,
  };
}
